import React from 'react';
import Select, { ValueType } from 'react-select';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Paper from '@material-ui/core/Paper';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { Avatar, Button, FormControl, Grid, InputBase, InputLabel, Switch, Typography } from '@material-ui/core';
import HandlePasswordReset from './HandlePasswordReset';
import DeleteAccountDialog from './DeleteAccountDialog';
import { DateFormat, DateFormatType, dateFormatList, DateFormatLookup } from "../general/datetime-formatter/DateFormat";
import { DeltaXMLColors, MyDeltaColors } from './styling/DeltaColors';
import { TimeFormat, timeFormatList, TimeFormatLookup } from '../general/datetime-formatter/TimeFormat';

const BootstrapInput = withStyles((theme: Theme) =>
  createStyles({
    root: {
      'label + &': {
        marginTop: theme.spacing(3),
      },
    },
    input: {
      borderRadius: 4,
      position: 'relative',
      backgroundColor: theme.palette.background.paper,
      border: '1px solid ' + DeltaXMLColors.OXFORD_BLUE,
      fontSize: 16,
      padding: '10px',
      width: '240px',
      '&:focus': {
        // Set border color
        border: '1px solid ' + DeltaXMLColors.CELERY
      }
    },
    error: {
      borderRadius: 4,
      position: 'relative',
      backgroundColor: theme.palette.background.paper,
      border: '1px solid ' + DeltaXMLColors.VALENTINE_RED,
      fontSize: 16,
      width: '262px'
    },
  }),
)(InputBase);


const styles = (theme: Theme) =>
  createStyles({
    paper: {
      maxWidth: 936,
      margin: 'auto',
      overflow: 'hidden',
    },
    searchBar: {
      borderBottom: '1px solid '+ MyDeltaColors.MERCURY,
      backgroundColor: DeltaXMLColors.DARK_BACKGROUND
    },
    contentWrapper: {
      margin: '40px 16px',
    },
    margin: {
      margin: theme.spacing(2),
    },
    inputLabel: {
      fontSize: '17',
      width: '150px',
      color: DeltaXMLColors.GRAY
    },
    button: {
      margin: '2px',
    },
    avatar: {
      width: theme.spacing(16),
      height: theme.spacing(16),
      fontSize: '45px',
      justifyContent: "center"
    },
    avatarGrid: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'right'
    },
    profileLabels: {
      margin: '24px 0px 0px 0px',
      height: '41px',
      width: '240px',
      fontWeight: 500,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: DeltaXMLColors.OXFORD_BLUE
    },
    buttonBar: {
      borderTop: '1px solid '+ MyDeltaColors.MERCURY,
      backgroundColor: DeltaXMLColors.CONCRETE,
      alignItems: "flex-end",
      top: 'auto',
      bottom: 0
    },
    dropDown: {
      margin: '24px 0px 0px 0px',
      height: '41px',
      width: '240px',
    },
    switch: {
      margin: '24px 0px 0px 0px',
    }
  });

export interface ContentProps extends WithStyles<typeof styles> {
  profileDetails: ProfileDetails,
  handleChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void
  updateProfileMode: boolean,
  handleUpdateProfileMode: (event: React.MouseEvent<HTMLButtonElement>) => void,
  handleSaveProfileDetails: (event: React.MouseEvent<HTMLButtonElement>) => void,
  handleCancelProfileMode: (event: React.MouseEvent<HTMLButtonElement>) => void
}

export interface ProfileDetails {
  givenName: string,
  familyName: string,
  compName: string,
  phone: string,
  emailAddress: string,
  dateFormat: string,
  timeFormat: string;
  avatarPicture: string;
  error: ErrorData
}

export interface ErrorData {
  phone: boolean
}


function ProfileContent(props: ContentProps) {
  const { classes, profileDetails, handleChange, updateProfileMode, handleUpdateProfileMode, handleSaveProfileDetails, handleCancelProfileMode } = props;
  const [openPasswordDialog, setOpenPasswordDialog] = React.useState(false);
  const [openDeleteAcctDialog, setOpenDeleteAcctDialog] = React.useState(false);
  const [selectedDateFormat, setSelectedDateFormat] = React.useState<ValueType<DateFormatType, false>>(DateFormatLookup(profileDetails.dateFormat));
  const [selectedTimeFormat, setSelectedTimeFormat] = React.useState<ValueType<DateFormatType, false>>(TimeFormatLookup(profileDetails.timeFormat));

  const handlePasswordDialogOpen = (isOpen: boolean) => {
    setOpenPasswordDialog(isOpen);
  };

  const handleDropDownChange = (dateFormat: ValueType<DateFormatType, false>) => {
    setSelectedDateFormat(dateFormat);
    profileDetails.dateFormat = dateFormat?.value === undefined ? DateFormat.Unix : dateFormat?.value;
  };

  const handleTimeDropDownChange = (timeFormat: ValueType<DateFormatType, false>) => {
    setSelectedTimeFormat(timeFormat);
    profileDetails.timeFormat = timeFormat?.value === undefined ? TimeFormat.AmPm : timeFormat?.value;
  };

  return (
    <Paper className={classes.paper}>
      <AppBar className={classes.searchBar} position="static" elevation={0}>
        <Toolbar>
          <Typography variant="h4">My Profile</Typography>
        </Toolbar>
      </AppBar>
      <div className={classes.contentWrapper}>
        <Grid container>
          <Grid item xs={4}>
            {updateProfileMode ?
              <FormControl className={classes.margin}>
                <InputLabel className={classes.inputLabel} htmlFor="demo-customized-textbox" shrink={true}>Given Name</InputLabel>
                <BootstrapInput name="givenName" value={profileDetails.givenName} onChange={handleChange} />
              </FormControl>
              :
              <FormControl className={classes.margin}>
                <InputLabel className={classes.inputLabel} htmlFor="demo-customized-textbox" shrink={true}>Name</InputLabel>
                <Typography className={classes.profileLabels}>{profileDetails.givenName + " " + profileDetails.familyName}</Typography>
              </FormControl>
            }
            <br />
            <FormControl className={classes.margin}>
              <InputLabel className={classes.inputLabel} htmlFor="demo-customized-textbox" shrink={true}>Company Name</InputLabel>
              {updateProfileMode ? <BootstrapInput name="compName" value={profileDetails.compName} onChange={handleChange} /> : <Typography className={classes.profileLabels}>{profileDetails.compName}</Typography>}
            </FormControl>
            <br />
            <FormControl className={classes.margin}>
              <InputLabel className={classes.inputLabel} htmlFor="demo-customized-textbox" shrink={true}>Phone</InputLabel>
              {updateProfileMode ? <BootstrapInput error={profileDetails.error.phone} name="phone" value={profileDetails.phone} onChange={handleChange} /> : <Typography className={classes.profileLabels}>{profileDetails.phone}</Typography>}
            </FormControl>
            <br />
            <FormControl className={classes.margin}>
              <InputLabel className={classes.inputLabel} htmlFor="demo-customized-textbox" shrink={true}>Email Address</InputLabel>
              {updateProfileMode ? <BootstrapInput name="emailAddress" value={profileDetails.emailAddress} onChange={handleChange} disabled/> : <Typography className={classes.profileLabels}>{profileDetails.emailAddress}</Typography>}
            </FormControl>
            <br />
            <FormControl className={classes.margin}>
              <InputLabel className={classes.inputLabel} htmlFor="demo-customized-textbox" shrink={true}>Date Format</InputLabel>
              {updateProfileMode ?
                <Select className={classes.dropDown} name="dateFormat" value={selectedDateFormat} options={dateFormatList} onChange={dateFormat => handleDropDownChange(dateFormat)} isSearchable={false}/>
                :
                <Typography className={classes.profileLabels}>{selectedDateFormat?.label}</Typography>
              }
            </FormControl>
            <br />
            <FormControl className={classes.margin}>
              <InputLabel className={classes.inputLabel} htmlFor="demo-customized-textbox" shrink={true}>Time Format</InputLabel>
              {updateProfileMode ?
                <Select className={classes.dropDown} name="timeFormat" value={selectedTimeFormat} options={timeFormatList} onChange={timeFormat => handleTimeDropDownChange(timeFormat)} isSearchable={false}/>
                :
                <Typography className={classes.profileLabels}>{selectedTimeFormat?.label}</Typography>
              }
            </FormControl>
            <br /><br />
          </Grid>
          <Grid item xs={4}>
            {updateProfileMode &&
              <FormControl className={classes.margin}>
                <InputLabel className={classes.inputLabel} htmlFor="demo-customized-textbox" shrink={true}>Family Name</InputLabel>
                <BootstrapInput name="familyName" value={profileDetails.familyName} onChange={handleChange} />
              </FormControl>
            }
          </Grid>
          <Grid item xs={4} className={classes.avatarGrid}>
            <Avatar className={classes.avatar} src={profileDetails.avatarPicture} />
          </Grid>
        </Grid>
      </div>
      <HandlePasswordReset open={openPasswordDialog} handleOpen={handlePasswordDialogOpen} />
      <DeleteAccountDialog open={openDeleteAcctDialog} handleClose={() => setOpenDeleteAcctDialog(false)} />
      <AppBar className={classes.buttonBar} position="static" elevation={0}>
        <Toolbar>
          {updateProfileMode ?
          <>
          <Button variant="contained" color="primary" onClick={handleSaveProfileDetails} className={classes.button} disabled={profileDetails.error.phone}>
              Save Profile
          </Button>
            <Button variant="contained" color="primary" className={classes.button} onClick={handleCancelProfileMode}>
                      Cancel
           </Button> 
          </> :
            <>
              <Button variant="contained" color="primary" onClick={handleUpdateProfileMode} className={classes.button} >
                Update Profile
          </Button>
              <Button variant="contained" color="primary" className={classes.button} onClick={() => handlePasswordDialogOpen(true)}>
                Change Password
          </Button>
              <Button variant="contained" color="primary" className={classes.button} onClick={() => setOpenDeleteAcctDialog(true)}>
                Delete Account
          </Button>
            </>
          }
        </Toolbar>
      </AppBar>
    </Paper>
  );
}

export default withStyles(styles)(ProfileContent);
